<template>
  <Toast />

  <div class="page-container">
    <div class="header">
      <h1>Global Catalog</h1>
      <p>Select assets and submit your purchase</p>
    </div>

    <!-- EMPTY STATE -->
    <div v-if="!data || data.length === 0" class="empty-state">
      <div class="empty-icon">📦</div>
      <h2>No assets available</h2>
      <p>Please check back later or refresh the catalog.</p>
    </div>

    <!-- GRID -->
    <div v-else class="stats-grid">
      <div class="stat-card" v-for="item in data" :key="item['@id']">
        <div class="card-header">
          <label class="pull-checkbox">
            <input
              type="checkbox"
              :checked="selectedAssets.includes(item['@id'])"
              @change="toggleSelect(item['@id'])"
            />
            <span class="checkbox-mark"></span>
          </label>

          <div class="title-group">
            <h2>{{ item.name }}</h2>
            <span class="participant">
              {{ item["dspace:participantId"] }}
            </span>
          </div>
        </div>

        <div class="card-body">
          <div class="info-row">
            <span class="label">Endpoint</span>
            <span class="value truncate">
              {{ item["dcat:service"]["dcat:endpointURL"] }}
            </span>
          </div>

          <div class="price-box">
            <span class="price">
              {{ item["pricingPlans"]["price"] }}
            </span>
            <span class="currency">
              {{ item["pricingPlans"]["priceCurrency"] }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ACTION BAR -->
    <div class="action-bar">
      <Button label="Acquire asset" @click="submitAsset()" />
    </div>
    <div class="action-bar">
      <Button label="Create Credential" @click="submitAsset()" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import axios from "axios";
import Button from "primevue/button";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";

const toast = useToast();
const data = ref(null);
const selectedAssets = ref([]);
const catalog = ref(null);

const toggleSelect = (id) => {
  if (selectedAssets.value.includes(id)) {
    selectedAssets.value = selectedAssets.value.filter(
      (assetId) => assetId !== id
    );
  } else {
    selectedAssets.value.push(id);
  }
};

const submitAsset = async () => {
  console.log("Selected asset IDs:", catalog.value);

  const catalogAssets = data.value.filter((item) =>
    selectedAssets.value.includes(item["@id"])
  );

  console.log("Catalog assets: ", catalogAssets);

  catalogAssets.forEach((asset) => {
    const exists = catalog.value.selectedAssets.some(
      (a) => a["@id"] === asset["@id"]
    );
    if (!exists) {
      catalog.value.selectedAssets.push(asset);
    }
  });

  if (catalog.value.selectedAssets.length === 0) {
    alert("Please select at least one asset to submit.");
    return;
  }

  const selectedAssetsRes = await axios.post(
    "http://localhost:3000/selected-assets",
    catalog.value,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Basic ZHMyLXBhcnRpY2lwYW50Om1ja1VzckFrbnQ=",
      },
    }
  );

  if (selectedAssetsRes.status === 201) {
    toast.add({
      severity: "success",
      summary: "Success",
      detail: "Asset successfully purchased!",
      life: 3000,
    });
  } else {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to submit selected assets.",
      life: 3000,
    });
  }
};

const fetchData = async () => {
  const response = await axios.get("http://localhost:3000/catalog", {
    headers: {
      "Content-Type": "application/json",
      Authorization: "Basic ZHMyLXBhcnRpY2lwYW50Om1ja1VzckFrbnQ=",
    },
  });

  catalog.value = {
    Title: response.data.title,
    selectedAssets: response.data.selectedAssets,
  };

  data.value = response.data.dataset;
};

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.page-container {
  padding: 32px;
  background: #f8fafc;
  min-height: 100vh;
}

/* HEADER */
.header {
  margin-bottom: 24px;
}

.header h1 {
  font-size: 36px;
  margin-bottom: 6px;
}

.header p {
  color: #64748b;
}

/* GRID */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

/* CARD */
.stat-card {
  background: white;
  border-radius: 18px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}

/* CARD HEADER */
.card-header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.title-group h2 {
  font-size: 18px;
  margin: 0;
}

.participant {
  font-size: 12px;
  color: #64748b;
}

/* BODY */
.card-body {
  margin-top: 16px;
}

/* INFO ROW */
.info-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.label {
  font-size: 12px;
  color: #94a3b8;
}

.value {
  font-size: 13px;
  color: #0f172a;
}

/* TRUNCATE */
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* PRICE */
.price-box {
  margin-top: 10px;
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.price {
  font-size: 22px;
  font-weight: 600;
}

.currency {
  font-size: 14px;
  color: #64748b;
}

/* CHECKBOX */
.pull-checkbox {
  position: relative;
  cursor: pointer;
}

.pull-checkbox input {
  opacity: 0;
  position: absolute;
}

.checkbox-mark {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid #cbd5f5;
  display: inline-block;
  transition: all 0.2s ease;
}

.pull-checkbox input:checked + .checkbox-mark {
  background: #2563eb;
  border-color: #2563eb;
}

.pull-checkbox input:checked + .checkbox-mark::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* ACTION BAR */
.action-bar {
  position: sticky;
  bottom: 0;
  margin-top: 30px;
  padding-top: 16px;
  display: flex;
  justify-content: flex-end;
  background: linear-gradient(to top, #f8fafc, transparent);
}

/* EMPTY STATE */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #64748b;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}
</style>
